// A textarea that grows to its content, for the laatu form's five free-text fields.
//
// 🔑 Why this exists rather than a smaller fixed height: the measured notes are short (median 25
// characters on an area, p90 75) and the outliers are long (max 879), so any single height is either
// five empty lines or a scrollbar. See `.laatu-notes` in `css/site.scss` for the census — and for why
// that class exists instead of an override of `.notes`, which an `!important` made unwinnable.
//
// ⚠️ A CSSOM write (`el.style.height = …`) is NOT governed by CSP — only markup `style=` attributes
// and `<style>` elements are, which is what `U-81` removed `unsafe-inline` for. So this is allowed
// where an inline attribute would be refused silently.
//
// 🚨 `scrollHeight` is 0 for an element inside a `display: none` ancestor, and every one of these
// fields starts inside a collapsed accordion section. Sizing one then would collapse it to
// `min-height` and leave it there, so `offsetParent` is checked first and `shown.bs.collapse` is
// what sizes the fields the user is about to see.
const grow = (el) => {
  if (!el || !el.offsetParent) { return }

  el.style.height = 'auto'
  el.style.height = `${el.scrollHeight}px`
}

// 📌 **Three passes, so the whole set costs ONE forced layout rather than two per element**
// (`/simplify`, 2026-10-28). Interleaving write/read/write per textarea forced a synchronous layout on
// each; a laatu form carries up to 13 of these (4 fixed plus one per area), and this runs after every
// save round trip on the one surface that is only ever used on a phone.
// ⚠️ The `offsetParent` filter has to come first and stay: `scrollHeight` is 0 inside a `display: none`
// ancestor, so sizing a collapsed field would pin it at `min-height` and leave it there.
const growAll = (root) => {
  if (!root) { return }

  const visible = Array.from(root.querySelectorAll('textarea.laatu-notes')).filter((el) => el.offsetParent)
  visible.forEach((el) => { el.style.height = 'auto' })
  const heights = visible.map((el) => el.scrollHeight)
  visible.forEach((el, i) => { el.style.height = `${heights[i]}px` })
}

export default (containerSelector) => {
  const container = document.querySelector(containerSelector)
  if (!container) { return }

  // Delegated, and re-bound per `turbolinks:load` like every other handler here: the form is replaced
  // wholesale by `edit_laatu.js.erb`, so nothing may be bound to the fields themselves.
  $(container).off('.autoGrow')
  $(container).on('input.autoGrow', 'textarea.laatu-notes', function () { grow(this) })
  // 🚨 NOT `shown.bs.collapse.autoGrow`. jQuery fires a handler only when every namespace it was
  // bound with is present in the trigger, and Bootstrap triggers `shown.bs.collapse` — so adding a
  // third namespace makes the handler silently never run. It is cleared by the explicit `off` below
  // instead, which is scoped to this container and is the only collapse binding on it.
  $(container).off('shown.bs.collapse')
  $(container).on('shown.bs.collapse', function (event) { growAll(event.target) })
  // `edit_laatu.js.erb` replaces the whole form inside this container, which leaves the delegated
  // handlers above intact and the new fields unsized. It triggers this rather than the pack
  // re-initialising, because a Turbolinks visit is not what happened.
  $(container).on('laatu:sizeNotes.autoGrow', function () { growAll(container) })

  growAll(container)
}
